import { Directive, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { Asset, CustomerBaseData, InstrumentType } from '@zero/shared/util-models';
import { CheckExperienceDialogNameType, TradeService } from '@zero/trade/domain';
import { Subscription, switchMap } from 'rxjs';

export interface DialogMode {
  experienceNoticeNoInfoProvided: boolean;
  experienceNoticeLeverage: boolean;
  experienceNoticeNonLeverage: boolean;
}

@Directive()
export class CheckExperienceAndFtgBase implements OnInit, OnDestroy, OnChanges {
  @Input() public assetToCheck: Asset;
  @Input() public showCancelButton: boolean;
  @Output() public allowedToProceedOrderChange = new EventEmitter<boolean>();
  @Output() public experienceCheckCanceled = new EventEmitter();

  public allowedToProceedOrder: boolean;
  public dialogMode: DialogMode;
  protected dialogsToShow: Record<string, boolean> = {};
  public allowedToActivateFtgInSelfService = false;

  private _baseDataSubscription: Subscription;
  private _customerBaseData: CustomerBaseData;

  protected customerStateFacade = inject(CustomerStateFacade);
  protected tradeService = inject(TradeService);

  public ngOnInit(): void {
    this.dialogMode = this.getInitialDialogMode();
    this._baseDataSubscription = this.customerStateFacade
      .loadCustomerBaseData()
      .pipe(switchMap(() => this.customerStateFacade.customerBaseData$))
      .subscribe((response) => this.check(response));
    if (this.showCancelButton === undefined) {
      this.showCancelButton = true;
    }
  }

  public ngOnDestroy(): void {
    this.dialogMode = this.getInitialDialogMode();
    this._baseDataSubscription?.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['assetToCheck']) {
      return;
    }

    if (changes['assetToCheck'].currentValue && changes['assetToCheck'].currentValue['experienceAndFtgOK'] === 'OK') {
      this.allowedToProceedOrder = true;
    } else {
      this.checkExperience();
    }
  }

  private check = (customerBaseData: CustomerBaseData): void => {
    this._customerBaseData = customerBaseData;
    this.allowedToActivateFtgInSelfService = Boolean(this._customerBaseData?.fzContractActiveDate);
    if (!this.allowedToProceedOrder) {
      this.checkExperience();
    }
  };

  private getInitialDialogMode(): DialogMode {
    return {
      experienceNoticeNoInfoProvided: false,
      experienceNoticeLeverage: false,
      experienceNoticeNonLeverage: false,
    };
  }

  private handleLeveragedProducts = (): void => {
    const { ftgPresent, orderCountLeveragedDerivatives } = this._customerBaseData;
    this.dialogsToShow['ftgNotice'] = !ftgPresent;
    if (!this.undismissableDialogPresent()) {
      if (orderCountLeveragedDerivatives < 10) {
        this.dialogsToShow['experienceNotice'] = true;
        this.dialogMode.experienceNoticeLeverage = true;
        if (typeof orderCountLeveragedDerivatives !== 'number') {
          this.dialogMode.experienceNoticeNoInfoProvided = true;
        }
      }
    }
  };

  private handleOtcProducts = (): void => {
    const { otcConfirmed } = this._customerBaseData;
    this.dialogsToShow['otcNotice'] = !otcConfirmed;
  };

  private handleCryptos = (): void => {
    const { hasActiveWallet, orderCountKrypto } = this._customerBaseData;
    if (!hasActiveWallet) {
      this.dialogsToShow['noWalletWarning'] = true;
      return;
    }

    if (orderCountKrypto < 10) {
      this.dialogsToShow['experienceNoticeKrypto'] = true;
    }
  };

  private handleComplexProducts = (): void => {
    const { orderCountNonLeveragedDerivatives } = this._customerBaseData;
    if (orderCountNonLeveragedDerivatives < 10) {
      this.dialogMode.experienceNoticeNonLeverage = true;
      this.dialogsToShow['experienceNotice'] = true;
      if (typeof orderCountNonLeveragedDerivatives !== 'number') {
        this.dialogMode.experienceNoticeNoInfoProvided = true;
      }
    }
  };

  private checkExperience(): void {
    if (!this.assetToCheck || !this._customerBaseData) {
      this.dialogMode = this.getInitialDialogMode();
      this.allowedToProceedOrder = false;
      return;
    }

    this.allowedToProceedOrder = false;
    if (this.assetToCheck.leverage) {
      this.handleLeveragedProducts();
    } else if (this.assetToCheck.complex) {
      this.handleComplexProducts();
    }

    if (this.assetToCheck.isOTC && !this.undismissableDialogPresent()) {
      this.handleOtcProducts();
    }
    if (this.assetToCheck.instrumentType === InstrumentType.Crypto) {
      this.handleCryptos();
    }

    this.checkForDialogsToShow();
  }

  private checkForDialogsToShow(): void {
    const areAnyMoreDialogToShow = this.anyMoreDialogToShow();

    if (!areAnyMoreDialogToShow && !this.undismissableDialogPresent()) {
      this.allowedToProceedOrder = true;
      this.allowedToProceedOrderChange.emit(this.allowedToProceedOrder);
    } else {
      this.showAlertIfNecessary();
    }
  }

  private anyMoreDialogToShow() {
    let anyMoreDialogToShow = false;
    for (const k of Object.keys(this.dialogsToShow)) {
      if (this.dialogsToShow[k]) {
        anyMoreDialogToShow = true;
        break;
      }
    }
    return anyMoreDialogToShow;
  }

  public proceed(dialogName: CheckExperienceDialogNameType): void {
    this.dialogClosed();

    if (this.isDismissableDialog(dialogName)) {
      this.dialogsToShow[dialogName] = false;
    }

    //combined notice ftg and experience
    if (dialogName === 'ftgNotice') {
      this.dialogsToShow['experienceNotice'] = false;
    }

    this.checkForDialogsToShow();
  }

  public async proceedAndConfirmOtc(): Promise<void> {
    this.dialogClosed();
    const response = await this.tradeService.confirmOtcTrading();
    if (response) {
      this.customerStateFacade.loadCustomerBaseData(true);
      this.proceed('otcNotice');
    } else {
      console.error('could not confirm otc trading');
      this.checkForDialogsToShow();
    }
  }

  public cancel(): void {
    this.dialogClosed();
    this.experienceCheckCanceled.emit();
  }

  protected showAlertIfNecessary() {
    // the magic happens in the overridden
  }

  protected dialogClosed() {
    // only needed in implementation for mobile app
  }

  private undismissableDialogPresent(): boolean {
    return this.dialogsToShow['noWalletWarning'];
  }

  private isDismissableDialog(dialogName: CheckExperienceDialogNameType) {
    return dialogName !== 'noWalletWarning';
  }
}
